import React from 'react';
import './ResearchProjectSalaryTable.css';

interface Position {
  id: string;
  name: string;
  fix: number;
  sti: number;
  ltip: string | number;
  reliability_score: number;
}

interface Company {
  id: string;
  name: string;
  sector: string;
  positions: Position[];
}

interface ResearchProjectSalaryTableProps {
  companies: Company[];
  onFilteredCountChange?: (count: number) => void;
}

const ResearchProjectSalaryTable: React.FC<ResearchProjectSalaryTableProps> = ({ companies, onFilteredCountChange }) => {
  const [selectedSectorFilter, setSelectedSectorFilter] = React.useState<string>('all');
  const [selectedPositionFilter, setSelectedPositionFilter] = React.useState<string>('');
  const [selectedDataType, setSelectedDataType] = React.useState<string>('fix');

  // Получаем уникальные сектора
  const sectors = companies.reduce((acc: string[], company) => {
    if (!acc.includes(company.sector)) {
      acc.push(company.sector);
    }
    return acc;
  }, []);
  
  // Получаем уникальные должности (useMemo для стабильной ссылки в deps useEffect)
  const positions = React.useMemo(
    () => (companies.length > 0 ? companies[0].positions.map(pos => ({ id: pos.id, name: pos.name })) : []),
    [companies]
  );
  
  // Устанавливаем первую должность по умолчанию, если должность не выбрана
  React.useEffect(() => {
    if (positions.length > 0 && !selectedPositionFilter) {
      setSelectedPositionFilter(positions[0].id);
    }
  }, [positions, selectedPositionFilter]);

  // Фильтруем компании по выбранным фильтрам
  const getFilteredCompanies = () => {
    let filtered = companies;
    
    // Фильтр по сектору
    if (selectedSectorFilter !== 'all') {
      filtered = filtered.filter(company => company.sector === selectedSectorFilter);
    }
    
    return filtered;
  };

  const filteredCompanies = getFilteredCompanies();

  // Уведомляем родительский компонент об изменении количества отфильтрованных компаний
  React.useEffect(() => {
    if (onFilteredCountChange) {
      onFilteredCountChange(filteredCompanies.length);
    }
  }, [filteredCompanies.length, onFilteredCountChange]);

  // Определяем количество перцентилей в зависимости от количества компаний
  const shouldShowReducedPercentiles = filteredCompanies.length < 10;
  const percentiles = shouldShowReducedPercentiles ? [25, 50, 75] : [10, 25, 40, 50, 60, 75, 90];
  const percentileLabels = shouldShowReducedPercentiles 
    ? ['25%', '50%', '75%'] 
    : ['10%', '25%', '40%', '50%', '60%', '75%', '90%'];

  // Получаем данные для выбранной должности
  const getPositionData = () => {
    if (filteredCompanies.length === 0 || !selectedPositionFilter) {
      return [];
    }
    
    // Получаем данные по выбранной должности из отфильтрованных компаний
    return filteredCompanies.map(company => {
      const position = company.positions.find(pos => pos.id === selectedPositionFilter);
      if (!position) return null;
      
      if (selectedDataType === 'fix') {
        return position.fix;
      } else if (selectedDataType === 'sti') {
        return position.sti;
      } else if (selectedDataType === 'total') {
        return position.fix + position.sti;
      }
      return null;
    }).filter(value => value !== null) as number[];
  };

  // Получаем данные FIX для выбранной должности
  const getFixData = () => {
    if (filteredCompanies.length === 0 || !selectedPositionFilter) {
      return [];
    }
    
    return filteredCompanies.map(company => {
      const position = company.positions.find(pos => pos.id === selectedPositionFilter);
      if (!position) return null;
      
      return position.fix;
    }).filter(value => value !== null) as number[];
  };

  // Получаем средний показатель надежности для отфильтрованных данных
  const getAverageReliability = () => {
    if (filteredCompanies.length === 0 || !selectedPositionFilter) {
      return 0;
    }
    
    const reliabilityScores = filteredCompanies.map(company => {
      const position = company.positions.find(pos => pos.id === selectedPositionFilter);
      return position ? position.reliability_score : 0;
    }).filter(score => score > 0);
    
    if (reliabilityScores.length === 0) {
      return 0;
    }
    
    return reliabilityScores.reduce((sum, score) => sum + score, 0) / reliabilityScores.length;
  };

  // Получаем текст и цвет надежности
  const getReliabilityInfo = () => {
    const avgReliability = getAverageReliability();
    
    if (avgReliability <= 0.3) {
      return {
        text: "Информация требует проверки (Low)",
        color: "#999999"
      };
    } else if (avgReliability > 0.3 && avgReliability <= 0.5) {
      return {
        text: "Умеренная достоверность (Moderate)",
        color: "#6d9eeb"
      };
    } else if (avgReliability > 0.5 && avgReliability <= 0.8) {
      return {
        text: "Высокая достоверность (High)",
        color: "#1155cc"
      };
    } else {
      return {
        text: "Очень высокая достоверность (Excellent)",
        color: "#073763"
      };
    }
  };

  const calculatePercentile = (data: number[], percentile: number): number => {
    if (data.length === 0) return 0;
    const sorted = [...data].sort((a, b) => a - b);
    const index = Math.ceil((percentile / 100) * sorted.length) - 1;
    return sorted[Math.max(0, index)];
  };

  const formatValue = (value: number, percentile: number): string => {
    const formattedValue = new Intl.NumberFormat('ru-RU', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value) + ' ₽';

    if (selectedDataType === 'sti') {
      // Для STI добавляем процент от базовой зарплаты (STI / FIX)
      const fixData = getFixData();
      if (fixData.length > 0) {
        const avgFix = fixData.reduce((sum, val) => sum + val, 0) / fixData.length;
        const percentage = avgFix > 0 ? (value / avgFix) * 100 : 0;
        
        return `${formattedValue} (${Math.round(percentage)}%)`;
      }
    } else if (selectedDataType === 'total') {
      // Для общей компенсации показываем только сумму без процента
      return formattedValue;
    }
    
    return formattedValue;
  };

  return (
    <div className="salary-table-container">
      <div className="salary-table-header">
        <h3 className="salary-table-title">
          {selectedDataType === 'fix' ? 'Базовая заработная плата (год)' : 
           selectedDataType === 'sti' ? 'Переменная компенсация (год)' : 
           'Общая компенсация (год)'}
        </h3>
        <p className="salary-table-subtitle">
          Представленная информация сформирована на основе данных, полученных в ходе полевых исследований команды аналитиков datacircle и регрессионных моделей ИИ-продукта datacircle
        </p>
      </div>
      
      <div className="salary-table-filters">
        <div className="filter-select-container">
          <select 
            className="filter-select"
            value={selectedSectorFilter}
            onChange={(e) => setSelectedSectorFilter(e.target.value)}
          >
            <option value="all">Все сектора</option>
            {sectors.map(sector => (
              <option key={sector} value={sector}>{sector}</option>
            ))}
          </select>
          <select 
            className="filter-select"
            value={selectedPositionFilter}
            onChange={(e) => setSelectedPositionFilter(e.target.value)}
          >
            {positions.map(position => (
              <option key={position.id} value={position.id}>{position.name}</option>
            ))}
          </select>
          <select 
            className="filter-select"
            value={selectedDataType}
            onChange={(e) => setSelectedDataType(e.target.value)}
          >
            <option value="fix">Базовая заработная плата (год)</option>
            <option value="sti">Переменная компенсация (год)</option>
            <option value="total">Общая компенсация (год)</option>
          </select>
        </div>
        <div style={{
          textAlign: 'left',
          marginTop: '12px',
          fontSize: '14px',
          color: '#666',
          fontFamily: "-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Arial, sans-serif"
        }}>
          Выбрано компаний: <strong>{filteredCompanies.length}</strong>
        </div>
      </div>
      
      <div className="salary-table-wrapper">
        <table className="salary-table">
          <thead>
            <tr>
              <th>Должность</th>
              <th>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>Надежность</span>
                  <button
                    style={{
                      background: 'none',
                      cursor: 'pointer',
                      padding: '6px',
                      borderRadius: '50%',
                      width: '24px',
                      height: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      color: '#1a237e',
                      backgroundColor: '#e3f2fd',
                      border: '1px solid #1a237e',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#1a237e';
                      e.currentTarget.style.color = 'white';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#e3f2fd';
                      e.currentTarget.style.color = '#1a237e';
                    }}
                    title="Информация о надежности данных"
                  >
                    ?
                  </button>
                </div>
              </th>
              {percentileLabels.map(label => (
                <th key={label}>{label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr className="salary-table-row">
              <td className="level-cell">
                <div className="level-info">
                  <span className="level-name">
                    {positions.find(p => p.id === selectedPositionFilter)?.name || 'Должность'}
                  </span>
                </div>
              </td>
                                <td className="consistency-cell">
                    <div className="consistency-indicator">
                      <div 
                        className="consistency-dot"
                        style={{ backgroundColor: getReliabilityInfo().color }}
                      ></div>
                      <span style={{ 
                        fontWeight: 'bold',
                        color: '#666',
                        fontSize: '12px',
                        lineHeight: '1.3',
                        display: 'inline-block',
                        maxWidth: '120px'
                      }}>
                        {getReliabilityInfo().text}
                      </span>
                    </div>
                  </td>
              {(() => {
                const positionData = getPositionData();
                const hasData = positionData.length > 0;
                const hasEnoughCompanies = filteredCompanies.length >= 3;
                
                if (!hasEnoughCompanies) {
                  return percentiles.map(percentile => (
                    <td key={percentile} className="percentile-cell">
                      {percentile === 25 || percentile === 50 || percentile === 75 ? (
                        <span style={{ 
                          color: '#666', 
                          fontSize: '11px',
                          fontStyle: 'italic',
                          display: 'inline-block',
                          maxWidth: '100px',
                          lineHeight: '1.2'
                        }}>
                          Недостаточно компаний<br />в выбранной референтной группе
                        </span>
                      ) : (
                        '-'
                      )}
                    </td>
                  ));
                }
                
                return percentiles.map(percentile => (
                  <td key={percentile} className="percentile-cell">
                    {hasData ? formatValue(calculatePercentile(positionData, percentile), percentile) : '-'}
                  </td>
                ));
              })()}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ResearchProjectSalaryTable; 